import {memo, useState, type MouseEvent, type ReactNode} from 'react';
import {
  Check,
  Copy,
  Film,
  History,
  Link,
  MoreHorizontal,
  PanelRightOpen,
  RotateCcw,
  ShieldCheck,
  StickyNote,
  Tag,
  Users,
  X
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useCopy
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {ImagesPurgedPlaceholder} from '@/components/shared/ImagesPurgedPlaceholder';
import {type Item} from '@/api/items';
import {getApiErrorMessage} from '@/api/client';
import {useFlagWithNote, useSetFlagNote} from '@/api/flagHooks';
import {showToast} from '@/lib/toast';
import {ChannelMenu} from '@/components/items/ChannelMenu';
import {type ImageMatchTier} from '@/api/jobs';
import {type FlagColor} from '@/api/flags';
import {FlagPicker} from '@/components/flags/Flag';
import {
  getSubmissionStatusTags,
  ImageMatchTierBadge,
  ItemQueueLeaseBadge,
  ChannelSubmittedBadge,
  SubmissionStatusTags,
  ThumbnailChangedBadge
} from '@/components/items/ItemBadges';
import {
  BLUR_IMAGE_CLASS,
  BlurIcon,
  GreyscaleIcon,
  SensitiveMediaControls,
  useSensitiveMedia
} from '@/components/shared/SensitiveMedia';
import {RdSignalsSection} from '@/components/items/RdSignalsSection';
import {ItemLanguage, ItemStats} from '@/components/items/ItemStats';
import {Thumbnail, useThumbnailLoadFailed} from '@/components/shared/Thumbnail';
import {getItemCardModel} from '@/lib/itemCard';
import {getDeclaredLanguageSubtag} from '@/lib/itemMetadata';
import {ACTOR_NOUN, ITEM_NOUN} from '@/lib/platform';
import {TONE_BADGE} from '@/lib/status';
import {worstStatusBorderClassName} from '@/lib/submissionStatusStyle';
import {itemPlatformId} from '@/lib/itemIds';
import {cn} from '@/lib/utils';
import {imageMatchIdFor} from '@/lib/imageMatchId';
import {useTranslatedText, useTranslatedTexts} from '@/store/translationStore';

export type ItemTermMatches = {
  readonly matchedInclude: readonly string[];
  readonly matchedExclude: readonly string[];
};

// Enough to keep the overlay from swallowing the thumbnail on a heavily-worded query - the rest
// collapse into the "+N" pill.
const MAX_VISIBLE_TERM_BADGES = 3;

// Tags shown before the rest collapse into a "+N" pill.
const MAX_VISIBLE_TAGS = 3;

const SHOW_MORE_CLASS =
  'text-muted-foreground hover:text-foreground mt-auto inline-flex items-center gap-1 self-end text-xs font-medium pointer-coarse:min-h-11';

// Icon buttons that sit on top of a thumbnail image - the one place a black/white overlay is the
// sanctioned treatment, since the image underneath is arbitrary. On a coarse pointer they grow to
// the 44px minimum touch target.
const OVERLAY_ICON_BUTTON =
  'bg-black/60 text-white hover:bg-black/80 hover:text-white pointer-coarse:size-11';

// The hover/focus/selected overlay: hidden only where hover exists to reveal it, so a touch screen
// (no hover) always shows it. Tab into any control inside and :focus-within reveals it too.
const OVERLAY_VISIBILITY =
  'transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100';

/** Which query terms this item's own text overlay shows, and how many more didn't fit. */
function TermMatchOverlay({
  matches,
  trailing
}: {
  readonly matches: ItemTermMatches;
  // Held at the strip's right end - the duration, which would otherwise sit under the strip.
  readonly trailing?: ReactNode;
}) {
  // Exclusion matches go first: an item that came back despite a `-term` is the more interesting
  // signal, and must not be the one pushed into the "+N" overflow by a long run of include terms.
  const badges: Array<{label: string; variant: 'secondary' | 'destructive'}> = [
    ...matches.matchedExclude.map((label) => ({label, variant: 'destructive' as const})),
    ...matches.matchedInclude.map((label) => ({label, variant: 'secondary' as const}))
  ];

  if (badges.length === 0) {
    return (
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-1 bg-black/70 px-1.5 py-1">
        <Badge variant="outline" className="border-white/30 bg-transparent text-white/70">
          no query terms found
        </Badge>
        <span className="ml-auto">{trailing}</span>
      </div>
    );
  }

  const visible = badges.slice(0, MAX_VISIBLE_TERM_BADGES);
  const overflow = badges.length - visible.length;

  return (
    <div className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center gap-1 bg-black/70 px-1.5 py-1">
      {visible.map((badge) => (
        <Badge
          key={badge.label}
          variant={badge.variant}
          // The design-system "destructive" variant is a low-opacity tint meant for a light card
          // background - unreadable stacked on this dark image backdrop. A solid fill only in this
          // one spot fixes the contrast without touching the shared Badge variant used elsewhere.
          className={
            badge.variant === 'destructive'
              ? 'bg-destructive border-transparent text-white'
              : undefined
          }>
          {badge.variant === 'destructive' ? '−' : ''}
          {badge.label}
        </Badge>
      ))}
      {overflow > 0 ? (
        <Badge variant="outline" className="border-white/30 bg-transparent text-white/70">
          +{overflow}
        </Badge>
      ) : null}
      <span className="ml-auto">{trailing}</span>
    </div>
  );
}

// The analyst's own flag on an item, as the same compact picker the submissions grid uses. Renders
// nothing for an Item the SPA built itself (flag absent), which has no server row to flag. Fenced
// off from the card's own click: React bubbles a click in the menu's portal up through the component
// tree, so without this picking a colour would also open Classify or toggle selection.
export function ItemFlagPicker({
  item,
  onSetFlag
}: {
  readonly item: Item;
  readonly onSetFlag: ((item: Item, color: FlagColor | null) => void) | undefined;
}) {
  const {mutate: setNote} = useSetFlagNote();
  const {mutate: flagWithNote} = useFlagWithNote();
  if (item.flag === undefined || !onSetFlag) {
    return null;
  }

  return (
    <span
      role="presentation"
      className="inline-flex shrink-0"
      onClick={(event) => {
        event.stopPropagation();
      }}>
      <FlagPicker
        value={item.flag}
        subject={item.title ?? item.external_id}
        note={item.flag_note ?? null}
        onNoteChange={(note) => {
          setNote(
            {itemId: item.id, note},
            {
              onError(error) {
                showToast(getApiErrorMessage(error, 'Failed to save note.'), 'error');
              }
            }
          );
        }}
        onChange={(color, note) => {
          if (color && note) {
            flagWithNote(
              {itemId: item.id, color, note},
              {
                onError(error) {
                  showToast(getApiErrorMessage(error, 'Failed to flag item.'), 'error');
                }
              }
            );
          } else {
            onSetFlag(item, color);
          }
        }}
      />
    </span>
  );
}

/* eslint-disable react/require-default-props -- memo() hides the defaults from the rule */
// A page's own icon action on a card, shown with the shared overlay buttons.
export type ItemExtraAction = {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly disabled?: boolean;
  readonly onSelect: (item: Item) => void;
};

export type ItemCardProps = {
  readonly item: Item;
  // Which of the callers' actions this card offers - a control with no handler isn't rendered.
  readonly onClassify?: (item: Item) => void;
  // Classifies the channel behind a video; only offered on non-channel items that know their channel.
  readonly onClassifyChannel?: (item: Item) => void;
  readonly onSetFlag?: (item: Item, color: FlagColor | null) => void;
  readonly onDismiss?: (item: Item) => void;
  readonly onToggleCheck?: (id: string) => void;
  // Click anywhere on the card that isn't one of its own controls (e.g. open Classify).
  readonly onOpen?: (item: Item) => void;
  readonly checked?: boolean;
  readonly anyChecked?: boolean;
  // Only provided for image_search jobs - how strongly each video's thumbnail matched the searched
  // image.
  readonly matchTiers?: ReadonlyMap<string, ImageMatchTier>;
  // Only provided when the caller has a search query to explain matches against.
  readonly matchedTerms?: ItemTermMatches;
  // The platform's own view of the content ('available' earns the shield-check). Not on Item - the
  // caller passes it when it knows.
  readonly platformStatus?: string | null;
  // Icon actions beside Dismiss in the overlay's action group.
  readonly extraActions?: readonly ItemExtraAction[];
  // Content under the card's body, for a page's own per-item detail.
  readonly renderFooter?: (item: Item) => ReactNode;
  // Opens this item in the caller's details sidebar. Without it, Show more expands the card in place.
  readonly onShowDetails?: (item: Item) => void;
  // The collection owns the one thumbnail-history dialog; the card only asks for it to open on its
  // item. Without a handler the control isn't rendered.
  readonly onShowHistory?: (item: Item) => void;
};
/* eslint-enable react/require-default-props */

// The one item card, shared by Items, Investigations and the job views.
//
// Wrapped in memo, and every callback takes the item/id rather than closing over it, so a parent
// can hand every card the SAME useCallback-stabilised function instead of a fresh closure per item
// per render - a job with a lot of items rendered (and re-rendered) every single card on any
// unrelated state change otherwise, with no way for a card to bail out.
export const ItemCard = memo(
  ({
    item,
    onClassify,
    onClassifyChannel,
    onSetFlag,
    onDismiss,
    onToggleCheck,
    onOpen,
    checked = false,
    anyChecked = false,
    matchTiers,
    matchedTerms,
    platformStatus,
    extraActions,
    renderFooter,
    onShowDetails,
    onShowHistory
  }: ItemCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const model = getItemCardModel(item);
    const translatedName = useTranslatedText(item.id, model.name);
    const description = useTranslatedText(item.id, model.description) ?? '';
    const tags = useTranslatedTexts(item.id, [...model.tags]);
    // Paired with the untranslated source tag for a React key: translation makes two distinct
    // source tags more likely to land on the same English word.
    const tagPairs = model.tags.map((raw, i) => ({raw, display: tags[i]!}));
    const {copy: copyId, isCopied: idCopied} = useCopy({value: () => itemPlatformId(item)});
    const {copy: copyUrl} = useCopy({value: () => item.url ?? ''});
    const isChannel = model.variant === 'channel' || model.variant === 'profile';
    const isPin = model.variant === 'pin';
    // The channel name becomes a menu offering Classify channel, for a video that knows its channel.
    const offersChannel = Boolean(onClassifyChannel && !isChannel && item.author_id);
    // Videos, pins and actor avatars share one Unblur/Blur pill, placed in this card's overlay.
    const {thumbnailProps: blurThumbnailProps, controls: mediaControls} = useSensitiveMedia();
    const initial = (item.author_handle ?? item.author_name ?? '?').charAt(0).toUpperCase();
    const avatarLoad = useThumbnailLoadFailed(item.thumbnail_url);
    const youtubeId = imageMatchIdFor(item);
    const matchTier = youtubeId ? matchTiers?.get(youtubeId) : undefined;
    const dismissed = item.item_status === 'dismissed';
    const submissionStatusTags = getSubmissionStatusTags(item.submission_status_groups);
    // The whole card border reads in the worst status tone at a glance instead of relying on the
    // small corner badge alone. Only a card that has a status gets a border at all.
    const statusBorderClassName = worstStatusBorderClassName(
      submissionStatusTags.map((tag) => tag.status)
    );
    const title = translatedName ?? model.name;
    // The design offers Translate beside the stats unless the item declares itself English; an
    // undeclared language may be anything.
    const translatable = [model.name, model.description, ...model.tags];
    const offerTranslate = getDeclaredLanguageSubtag(item.metadata) !== 'en';
    const showLanguage =
      model.language !== null ||
      (offerTranslate && translatable.some((text) => Boolean(text?.trim())));
    const hasInlineMore = description.length > 0 || tagPairs.length > MAX_VISIBLE_TAGS;
    const mediaControlsShown =
      item.thumbnail_url && !(isChannel && avatarLoad.failed) ? mediaControls : null;
    const durationTag = model.duration ? (
      <Badge variant="secondary" className="tabular-nums">
        {model.duration}
      </Badge>
    ) : null;
    const nounLabel = isChannel ? ACTOR_NOUN : ITEM_NOUN;
    const overlayVisible = checked || anyChecked;

    // The card is a click target only when the caller gave it somewhere to go; controls inside
    // stopPropagation so they never also fire it.
    const handleCardClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      onOpen?.(item);
    };

    return (
      <Card
        size="sm"
        selected={checked}
        className={cn(
          'group relative flex h-full flex-col gap-0 py-0',
          // Skips layout/paint for offscreen cards (native to the browser, no virtualization library
          // needed) once a group can hold hundreds of cards - contain-intrinsic-size is a rough guess
          // at a card's rendered height so the scrollbar doesn't jump before anything has painted.
          '[contain-intrinsic-size:auto_420px] [content-visibility:auto]',
          onOpen && 'cursor-pointer',
          dismissed && 'opacity-50',
          submissionStatusTags.length > 0 && 'border-2',
          submissionStatusTags.length > 0 && statusBorderClassName
        )}
        onClick={onOpen ? handleCardClick : undefined}>
        {/* Media: an avatar hero for a channel or profile, a 16:9 frame for a video, and a taller
            frame for a pin, which is mostly portrait and loses most of itself to a 16:9 crop. */}
        {/* Presentation: a click on the media toggles the selection, and the checkbox over it is the
            keyboard route to the same action. */}
        <div
          role="presentation"
          className={cn(
            'border-border bg-card @container relative w-full shrink-0 overflow-hidden border-b',
            isPin ? 'aspect-[4/5] max-h-60' : 'aspect-video'
          )}
          onClick={
            onToggleCheck && !onOpen
              ? () => {
                  onToggleCheck(item.id);
                }
              : undefined
          }>
          {checked ? (
            <div className="bg-primary/20 pointer-events-none absolute inset-0 z-10" />
          ) : null}

          {isChannel ? (
            <div className="flex h-full w-full items-center justify-center">
              {item.thumbnail_url && !avatarLoad.failed ? (
                // The Avatar frame around a Thumbnail rather than an AvatarImage: the blur
                // preference and the Unblur pill are wired through Thumbnail, which AvatarImage
                // cannot carry. Avatar's own edge sits on an ::after overlay, so pointer events
                // pass through it to the image beneath.
                <Avatar className="size-20 after:pointer-events-none">
                  <Thumbnail
                    src={item.thumbnail_url}
                    storedSha256={item.stored_thumbnail_sha256}
                    alt={item.title ?? ''}
                    loading="lazy"
                    className="aspect-square size-full rounded-full object-cover"
                    onError={avatarLoad.onError}
                    {...blurThumbnailProps}
                  />
                </Avatar>
              ) : item.images_purged ? (
                <ImagesPurgedPlaceholder reason={item.images_purged_reason} />
              ) : (
                <Avatar aria-hidden="true" className="size-20">
                  <AvatarFallback variant="primary" className="text-2xl">
                    {initial}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          ) : item.thumbnail_url ? (
            <Thumbnail
              src={item.thumbnail_url}
              storedSha256={item.stored_thumbnail_sha256}
              alt={item.title ?? ''}
              loading="lazy"
              className={cn(
                'h-full w-full object-cover',
                BLUR_IMAGE_CLASS,
                isPin && 'object-[center_20%]'
              )}
              {...blurThumbnailProps}
            />
          ) : item.images_purged ? (
            <ImagesPurgedPlaceholder reason={item.images_purged_reason} />
          ) : (
            <div className="text-muted-foreground flex h-full items-center justify-center">
              <Film className="size-8" />
            </div>
          )}

          {/* The length of a video (or video pin) is a tag in the media's bottom-right corner, as on
              the platform - at the end of the query-term strip when that holds the bottom edge. */}
          {matchedTerms ? (
            <TermMatchOverlay matches={matchedTerms} trailing={durationTag} />
          ) : model.duration ? (
            <div className="pointer-events-none absolute right-2 bottom-2 z-10">{durationTag}</div>
          ) : null}

          {/* Actor pill + submission status, top right. */}
          <div className="pointer-events-none absolute top-2 right-2 z-20 flex max-w-[70%] flex-col items-end gap-1">
            <ItemQueueLeaseBadge item={item} className="pointer-events-auto" />
            {isChannel ? (
              <Badge className="bg-black/70 text-white">
                <Users />
                {model.variant === 'profile' ? 'Profile' : 'Channel'}
              </Badge>
            ) : null}
            {item.thumbnail_changed ? <ThumbnailChangedBadge /> : null}
            <SubmissionStatusTags
              tags={submissionStatusTags}
              className="flex-col items-end"
              wrap={false}
            />
            {/* Top right with the other status tags, so it never crowds the channel name. */}
            {isChannel ? null : (
              <ChannelSubmittedBadge
                groups={item.channel_submission_status_groups}
                className="pointer-events-auto"
              />
            )}
          </div>

          {/* Hover/focus/selected overlay. Pointer-events pass through the wrapper so a click on
              bare thumbnail still reaches the Thumbnail (reveal) or the card; the buttons opt back in. */}
          <div
            className={cn(
              'pointer-events-none absolute inset-0 z-20',
              OVERLAY_VISIBILITY,
              overlayVisible && 'opacity-100!'
            )}>
            {onToggleCheck ? (
              // Set on a card-coloured block with an accent-coloured edge and a shadow (per the design
              // and its review), so the box stays visible over a pale or busy image. A click on the
              // block's padding counts too. rounded-lg is the radius step that nests around the
              // checkbox's rounded-sm across the 5px padding, and like every radius token it goes
              // square under the Sharp corners option.
              <div
                role="presentation"
                className="bg-card pointer-events-auto absolute top-2 left-2 flex rounded-lg p-[5px] shadow-[0_0_0_1px_var(--primary),0_1px_3px_rgb(0_0_0/0.3)]"
                onClick={(event) => {
                  event.stopPropagation();
                  if (event.target === event.currentTarget) {
                    onToggleCheck(item.id);
                  }
                }}>
                <Checkbox
                  checked={checked}
                  aria-label={`Select ${title}`}
                  className="pointer-coarse:size-6"
                  onCheckedChange={() => {
                    onToggleCheck(item.id);
                  }}
                />
              </div>
            ) : null}

            {/* Below 12rem of media width the pill and the tool row give way to the overflow menu. */}
            {mediaControlsShown ? (
              <SensitiveMediaControls {...mediaControlsShown} className="@max-[12rem]:hidden" />
            ) : null}

            {/* Bottom left: Classify moved into the card body, leaving the per-item tools here. */}
            {/* right-0 + flex-wrap: on a narrow card (browser zoom) the tools wrap onto a second
                row instead of being clipped by the media frame's overflow-hidden. */}
            <div className="absolute right-0 bottom-0 left-0 flex items-center p-2">
              <div className="pointer-events-auto flex flex-wrap items-center gap-1 @max-[12rem]:hidden">
                {onDismiss ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={dismissed ? 'Restore item' : 'Dismiss item'}
                          className={OVERLAY_ICON_BUTTON}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDismiss(item);
                          }}
                        />
                      }>
                      {dismissed ? <RotateCcw /> : <X />}
                    </TooltipTrigger>
                    <TooltipContent>{dismissed ? 'Restore item' : 'Dismiss item'}</TooltipContent>
                  </Tooltip>
                ) : null}
                {extraActions?.map((action) => (
                  <Tooltip key={action.key}>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          disabled={action.disabled ?? false}
                          aria-label={action.label}
                          className={OVERLAY_ICON_BUTTON}
                          onClick={(event) => {
                            event.stopPropagation();
                            action.onSelect(item);
                          }}
                        />
                      }>
                      {action.icon}
                    </TooltipTrigger>
                    <TooltipContent>{action.label}</TooltipContent>
                  </Tooltip>
                ))}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Copy ${nounLabel} ID`}
                        className={OVERLAY_ICON_BUTTON}
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyId();
                        }}
                      />
                    }>
                    {idCopied ? <Check /> : <Copy />}
                  </TooltipTrigger>
                  <TooltipContent>{idCopied ? 'Copied' : `Copy ${nounLabel} ID`}</TooltipContent>
                </Tooltip>
                {item.thumbnail_url && !isChannel && onShowHistory ? (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Thumbnail history"
                    className={OVERLAY_ICON_BUTTON}
                    onClick={(event) => {
                      event.stopPropagation();
                      onShowHistory(item);
                    }}>
                    <History />
                  </Button>
                ) : null}
                {item.url ? (
                  <CopyUrlButton
                    url={item.url}
                    label={`Copy ${nounLabel} URL`}
                    className={OVERLAY_ICON_BUTTON}
                  />
                ) : null}
              </div>
              <div className="pointer-events-auto hidden @max-[12rem]:flex">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Item actions"
                        className={OVERLAY_ICON_BUTTON}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    }>
                    <MoreHorizontal />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {mediaControlsShown ? (
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          mediaControlsShown.blur.onToggle();
                        }}>
                        <BlurIcon applied={mediaControlsShown.blur.applied} />
                        {mediaControlsShown.blur.applied ? 'Unblur' : 'Blur'}
                      </DropdownMenuItem>
                    ) : null}
                    {mediaControlsShown ? (
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          mediaControlsShown.greyscale.onToggle();
                        }}>
                        <GreyscaleIcon applied={mediaControlsShown.greyscale.applied} />
                        {mediaControlsShown.greyscale.applied ? 'Colourise' : 'Greyscale'}
                      </DropdownMenuItem>
                    ) : null}
                    {onDismiss ? (
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          onDismiss(item);
                        }}>
                        {dismissed ? <RotateCcw /> : <X />}
                        {dismissed ? 'Restore item' : 'Dismiss item'}
                      </DropdownMenuItem>
                    ) : null}
                    {extraActions?.map((action) => (
                      <DropdownMenuItem
                        key={action.key}
                        disabled={action.disabled ?? false}
                        onClick={(event) => {
                          event.stopPropagation();
                          action.onSelect(item);
                        }}>
                        {action.icon}
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        void copyId();
                      }}>
                      <Copy />
                      {`Copy ${nounLabel} ID`}
                    </DropdownMenuItem>
                    {item.thumbnail_url && !isChannel && onShowHistory ? (
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          onShowHistory(item);
                        }}>
                        <History />
                        Thumbnail history
                      </DropdownMenuItem>
                    ) : null}
                    {item.url ? (
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyUrl();
                        }}>
                        <Link />
                        {`Copy ${nounLabel} URL`}
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        {/* Body: who, what, when; then the stats, description and tags (per the v2 card). */}
        <CardContent className="flex flex-1 flex-col gap-2 px-3.5 pt-3 pb-3.5">
          <div className="flex items-center gap-2">
            <Avatar size="sm" aria-hidden="true">
              <AvatarFallback>{initial}</AvatarFallback>
            </Avatar>
            {offersChannel && model.handle ? (
              <div className="min-w-0 flex-1 truncate text-xs">
                <ChannelMenu
                  item={item}
                  className="text-muted-foreground max-w-full truncate text-xs"
                  onClassifyChannel={onClassifyChannel!}>
                  {model.handle}
                </ChannelMenu>
              </div>
            ) : (
              <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                {model.handle ?? '—'}
              </p>
            )}
            {platformStatus === 'available' ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    // A filled chip: --success is a surface token and fails AA as ink.
                    <span
                      className={cn(
                        TONE_BADGE.success,
                        'flex size-4 shrink-0 items-center justify-center'
                      )}
                    />
                  }>
                  <ShieldCheck className="size-3" aria-label="Available on platform" />
                </TooltipTrigger>
                <TooltipContent>Available on platform</TooltipContent>
              </Tooltip>
            ) : null}
            {matchTier ? <ImageMatchTierBadge tier={matchTier} /> : null}
            <ItemFlagPicker item={item} onSetFlag={onSetFlag} />
          </div>

          {/* Two lines reserved even for a short name, so the rows below line up across a grid row. */}
          <p className="text-foreground line-clamp-2 min-h-[2.6em] text-sm leading-[1.3] font-medium break-words">
            {title}
          </p>
          {item.flag_note ? (
            <p
              className="bg-primary/10 text-foreground border-primary flex items-start gap-1.5 rounded-md border-l-2 px-2 py-1 text-xs"
              title={item.flag_note}>
              <StickyNote aria-hidden className="text-primary-ink mt-0.5 size-3 shrink-0" />
              <span className="line-clamp-2">{item.flag_note}</span>
            </p>
          ) : null}

          {model.date || onClassify ? (
            <div className="flex min-h-6 items-center justify-between gap-2">
              {model.date ? (
                <p className="text-muted-foreground m-0 text-xs" title={model.date.title}>
                  {model.date.text}
                </p>
              ) : (
                <span />
              )}
              {onClassify ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto px-0 pointer-coarse:min-h-11"
                  aria-label={`Classify ${title}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClassify(item);
                  }}>
                  <Tag />
                  Classify
                </Button>
              ) : null}
            </div>
          ) : null}

          <div
            role="presentation"
            className="border-border mt-0.5 flex flex-1 flex-col gap-2 border-t pt-3"
            onClick={(event) => {
              // Translate, the description tooltip and Show more are controls of their own - they
              // must not also open Classify or toggle selection.
              event.stopPropagation();
            }}>
            {model.stats.length > 0 || showLanguage ? (
              <ItemStats
                stats={model.stats}
                className="border-border border-b pb-3"
                trailing={
                  <ItemLanguage
                    itemKey={item.id}
                    language={model.language}
                    texts={translatable}
                    offerTranslate={offerTranslate}
                  />
                }
              />
            ) : null}

            {description ? (
              <p
                className={cn(
                  'text-muted-foreground m-0 text-xs leading-normal break-words',
                  !expanded && 'line-clamp-2'
                )}>
                {description}
              </p>
            ) : null}

            {tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {(expanded ? tagPairs : tagPairs.slice(0, MAX_VISIBLE_TAGS)).map((pair) => (
                  <Badge key={pair.raw} variant="tag">
                    {pair.display}
                  </Badge>
                ))}
                {!expanded && tagPairs.length > MAX_VISIBLE_TAGS ? (
                  <Badge variant="tag" className="font-semibold">
                    +{tagPairs.length - MAX_VISIBLE_TAGS}
                  </Badge>
                ) : null}
              </div>
            ) : null}

            <RdSignalsSection itemId={item.id} assessment={item.rd_assessment} />

            {onShowDetails ? (
              <button
                type="button"
                aria-haspopup="dialog"
                className={SHOW_MORE_CLASS}
                onClick={() => {
                  onShowDetails(item);
                }}>
                Show more
                <PanelRightOpen className="size-3" />
              </button>
            ) : hasInlineMore ? (
              <button
                type="button"
                aria-expanded={expanded}
                className={SHOW_MORE_CLASS}
                onClick={() => {
                  setExpanded((value) => !value);
                }}>
                {expanded ? 'Show less' : 'Show more'}
              </button>
            ) : null}
          </div>
          {renderFooter?.(item)}
        </CardContent>
      </Card>
    );
  }
);
